import React from 'react';
import { BASEURL } from '@services/network';
import { createAuthedEventSource } from '@utils/streamAuth';
import { logError, logger } from '@utils/usefulFuncs';

const ACTIONS = {
  init: 'init',
  reset: 'reset',
  update: 'u',
  error: 'error',
  complete: 'complete',
};

function createLookupsFromV2Answers(answers) {
  const answersLookup = {};
  const contextLookup = {};
  (answers || []).forEach((row) => {
    if (!row || row.question_index == null) return;
    const idx = row.question_index;
    const existing = answersLookup[idx];
    if (existing != null && !row.final_answer) return;
    answersLookup[idx] = row.answer || '';
    contextLookup[idx] = row.context;
  });
  return { answersLookup, contextLookup };
}

function reducer(state, action) {
  switch (action.type) {
    case ACTIONS.reset: {
      return initialState;
    }

    case ACTIONS.init: {
      return action.payload;
    }

    case ACTIONS.update: {
      const { payload } = action;
      const streamData = typeof payload === 'string' ? JSON.parse(payload) : payload;
      const id = streamData?.question_index;
      if (id == null) return state;
      return {
        ...state,
        error: null,
        answersLookup: {
          ...(state.answersLookup || {}),
          [id]: streamData.answer || '',
        },
      };
    }

    case ACTIONS.error: {
      return { ...state, error: action.payload };
    }

    case ACTIONS.complete: {
      const { payload } = action;
      const { answersLookup, contextLookup } = createLookupsFromV2Answers(payload);
      return {
        ...state,
        streamEnd: true,
        answersLookup,
        contextLookup,
      };
    }

    default:
      throw new Error('Missing case');
  }
}

const getStreamingResponse = async (onCloseSource, resultId, dispatch) => {
  return new Promise((resolve, reject) => {
    if (!resultId) {
      return reject('resultId was missing');
    }

    let source = createAuthedEventSource(
      `${BASEURL}/api/v2/genai/queries/stream_events?run_id=${encodeURIComponent(resultId)}&include_context=true`
    );
    onCloseSource.call = () => {
      console.log('outside close source called');
      source.close(); // set outer ability to close stream if component unmounts
    };

    const onMessage = function (event) {
      try {
        const { data } = event;
        let parsed = null;
        try {
          parsed = JSON.parse(data);
        } catch {
          // Stream events can be non-JSON while the UI still appends the raw payload below.
        }
        logger('useNewCompanySnapshotStreamingResult onMessage', 'received', {
          runId: resultId,
          questionIndex: parsed?.question_index,
          finalAnswer: parsed?.final_answer === true,
          answerLength: parsed?.answer?.length || 0,
        });
        dispatch({
          type: ACTIONS.update,
          payload: data,
        });
      } catch (e) {
        logError('error caught in stream', e);
      }
    };
    const onOpen = function () {};
    const onError = function (e) {
      try {
        dispatch({
          type: ACTIONS.error,
          payload: 'Stream interrupted, retrying...',
        });
        logError('error received in stream', e);
        reject('error received in stream');
      } catch (e) {
        logError('error caught in stream', e);
        reject('error caught in stream');
      }
    };
    const onComplete = function (event) {
      try {
        const { data } = event;
        const streamData = JSON.parse(data);
        const answers = streamData?.response?.answers || [];
        dispatch({
          type: ACTIONS.complete,
          payload: answers,
        });

        source.removeEventListener('open', onOpen);
        source.removeEventListener('error', onError);
        source.removeEventListener('message', onMessage);
        source.close();
      } catch (e) {
        logError('error caught in stream', e);
        reject('error caught in stream');
      }
    };

    source.addEventListener('open', onOpen);
    source.addEventListener('error', onError);
    source.addEventListener('message', onMessage);
    source.addEventListener('complete', onComplete);
  });
};

const initialState = {
  contextLookup: null,
};

function convertQuestionListToQuestionListAndAnswerIds(questionList) {
  return questionList.map((q, i) => [q, i]);
}

export const useNewCompanySnapshotStreamingResult = (resultId, questionList) => {
  const [state, dispatch] = React.useReducer(reducer, initialState, () => initialState);

  React.useEffect(() => {
    if (questionList) {
      dispatch({
        type: ACTIONS.init,
        payload: {
          streamEnd: false,
          questionList,
          questionListAndAnswerIds: convertQuestionListToQuestionListAndAnswerIds(questionList),
          answersLookup: {},
        },
      });
    }
  }, [questionList]);

  React.useEffect(() => {
    const activeSources = [];
    const maxRetries = 2;
    const retryDelayMS = 1000;

    const getStreamingResponseWithRetries = (retriesLeft) => {
      const onCloseSource = {};
      activeSources.push(onCloseSource);

      getStreamingResponse(onCloseSource, resultId, dispatch).catch((error) => {
        if (retriesLeft > 0) {
          console.error(`Error occurred, retrying... Attempts left: ${retriesLeft}`);
          setTimeout(() => {
            getStreamingResponseWithRetries(retriesLeft - 1);
          }, retryDelayMS);
        }
      });
    };

    if (resultId) {
      getStreamingResponseWithRetries(maxRetries);
    }
    return () => {
      // Close all active sources
      activeSources.forEach((onCloseSource) => {
        onCloseSource?.call && onCloseSource?.call();
      });
      dispatch({
        type: ACTIONS.reset,
      });
    };
  }, [resultId]);

  return state;
};
